import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider, AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrganizationProvider, useOrganizationContext } from './contexts/OrganizationContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { DemoModeProvider } from './contexts/DemoModeContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { QueryProvider } from './providers/QueryProvider';
import { PostHogProvider } from './providers/PostHogProvider';
import { Login } from './pages/Login';
import { SignUp } from './pages/SignUp';
import { DashboardLayout } from './components/DashboardLayout';
import { SessionTimeoutModal } from './components/SessionTimeoutModal';
import { GlobalSearchModal } from './components/GlobalSearchModal';
import { PageLoader } from './components/PageLoader';
import ErrorBoundary from './components/ErrorBoundary';
import { Toaster } from 'sonner';
import { MotionConfig } from 'motion/react';
import { useState, useEffect, lazy, Suspense } from 'react';
import { clerkPublishableKey } from '../../utils/clerk/info';
import { logger } from './utils/logger';
import { logEnvironmentInfo, validateEnvironment } from './utils/environment';
import { initConsoleFilters } from './utils/consoleFilter';

// Initialize console filters early to suppress known warnings
initConsoleFilters();

// App version: 2.0.1
// Production-ready lazy loading for optimal performance
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })).catch(() => ({ default: () => <PageLoader /> })));
const Runs = lazy(() => import('./pages/Runs').then(m => ({ default: m.Runs })).catch(() => ({ default: () => <PageLoader /> })));
const RunDetail = lazy(() => import('./pages/RunDetail').then(m => ({ default: m.RunDetail })).catch(() => ({ default: () => <PageLoader /> })));
const NewRun = lazy(() => import('./pages/NewRun').then(m => ({ default: m.NewRun })).catch(() => ({ default: () => <PageLoader /> })));
const Pipeline = lazy(() => import('./pages/Pipeline').then(m => ({ default: m.Pipeline })).catch(() => ({ default: () => <PageLoader /> })));
const Contacts = lazy(() => import('./pages/Contacts').then(m => ({ default: m.Contacts })).catch(() => ({ default: () => <PageLoader /> })));
const Tasks = lazy(() => import('./pages/Tasks').then(m => ({ default: m.Tasks })).catch(() => ({ default: () => <PageLoader /> })));
const Jobs = lazy(() => import('./pages/Jobs').then(m => ({ default: m.Jobs })).catch(() => ({ default: () => <PageLoader /> })));
const JobDetail = lazy(() => import('./pages/JobDetail').then(m => ({ default: m.JobDetail })).catch(() => ({ default: () => <PageLoader /> })));
const NewJob = lazy(() => import('./pages/NewJob').then(m => ({ default: m.NewJob })).catch(() => ({ default: () => <PageLoader /> })));
const Analytics = lazy(() => import('./pages/Analytics').then(m => ({ default: m.Analytics })).catch(() => ({ default: () => <PageLoader /> })));
const Messages = lazy(() => import('./pages/Messages').then(m => ({ default: m.Messages })).catch(() => ({ default: () => <PageLoader /> })));
const TeamPerformance = lazy(() => import('./pages/TeamPerformance').then(m => ({ default: m.TeamPerformance })).catch(() => ({ default: () => <PageLoader /> })));
const DailyStandup = lazy(() => import('./pages/DailyStandup').then(m => ({ default: m.DailyStandup })).catch(() => ({ default: () => <PageLoader /> })));
const Blacklist = lazy(() => import('./pages/Blacklist').then(m => ({ default: m.Blacklist })).catch(() => ({ default: () => <PageLoader /> })));
const TargetAccounts = lazy(() => import('./pages/TargetAccounts').then(m => ({ default: m.TargetAccounts })).catch(() => ({ default: () => <PageLoader /> })));
const Integrations = lazy(() => import('./pages/Integrations').then(m => ({ default: m.Integrations })).catch(() => ({ default: () => <PageLoader /> })));
const Team = lazy(() => import('./pages/Team').then(m => ({ default: m.Team })).catch(() => ({ default: () => <PageLoader /> })));
const Billing = lazy(() => import('./pages/Billing').then(m => ({ default: m.Billing })).catch(() => ({ default: () => <PageLoader /> })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })).catch(() => ({ default: () => <PageLoader /> })));
const Help = lazy(() => import('./pages/Help').then(m => ({ default: m.Help })).catch(() => ({ default: () => <PageLoader /> })));

/**
 * Chrome Extension Auth Handler Component
 * Handles authentication callbacks from Chrome Extension
 */
// Allowed Chrome extension IDs — set VITE_ALLOWED_EXTENSION_IDS in your .env
const ALLOWED_EXTENSION_IDS: string[] = (
  import.meta.env.VITE_ALLOWED_EXTENSION_IDS as string | undefined ?? ''
)
  .split(',')
  .map((id) => id.trim())
  .filter(Boolean);

function ExtensionAuthHandler() {
  const { user, isAuthenticated, getToken } = useAuth();
  const { currentOrg } = useOrganizationContext();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isExtensionAuth = urlParams.get('extension_auth') === 'true';
    const requestingExtensionId = urlParams.get('extension_id') ?? '';

    if (!isExtensionAuth || !user || !isAuthenticated) return;

    // Reject if no allowed IDs are configured or the requesting ID is not in the allowlist
    if (
      ALLOWED_EXTENSION_IDS.length === 0 ||
      !ALLOWED_EXTENSION_IDS.includes(requestingExtensionId)
    ) {
      logger.warn('Extension auth rejected: extension ID not in allowlist', requestingExtensionId);
      return;
    }

    getToken()
      .then(token => {
        if (token && (window as unknown as { chrome?: { runtime?: { sendMessage?: unknown } } }).chrome?.runtime?.sendMessage) {
          (window as unknown as { chrome: { runtime: { sendMessage: (id: string, msg: unknown) => void } } }).chrome.runtime.sendMessage(
            requestingExtensionId,
            {
              action: 'auth_success',
              token: token,
              user: { id: user.id, name: user.name, email: user.email },
              org: currentOrg ? { id: currentOrg.id, name: currentOrg.name } : null,
            }
          );
          logger.info('Extension authenticated successfully');
          setTimeout(() => window.close(), 2000);
        }
      })
      .catch(err => {
        logger.error('Extension auth error:', err);
      });
  }, [user, isAuthenticated, getToken, currentOrg]);

  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isCurrentUserAdmin } = useOrganizationContext();

  if (!isCurrentUserAdmin()) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/sign-up" element={isAuthenticated ? <Navigate to="/" replace /> : <SignUp />} />
        {/* Clerk SSO callback routes */}
        <Route path="/login/sso-callback" element={<AuthenticateWithRedirectCallback />} />
        <Route path="/sign-up/sso-callback" element={<AuthenticateWithRedirectCallback />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="runs" element={<Runs />} />
          <Route path="runs/new" element={<NewRun />} />
          <Route path="runs/:id" element={<RunDetail />} />
          <Route path="pipeline" element={<Pipeline />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="jobs/new" element={<NewJob />} />
          <Route path="jobs/:id" element={<JobDetail />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="messages" element={<Messages />} />
          <Route path="team-performance" element={<TeamPerformance />} />
          <Route path="daily-standup" element={<DailyStandup />} />
          <Route path="blacklist" element={<Blacklist />} />
          <Route path="target-accounts" element={<TargetAccounts />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="team" element={<AdminRoute><Team /></AdminRoute>} />
          <Route path="billing" element={<AdminRoute><Billing /></AdminRoute>} />
          <Route path="settings" element={<AdminRoute><Settings /></AdminRoute>} />
          <Route path="help" element={<Help />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  const [sessionTimeout, setSessionTimeout] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Get Clerk publishable key from centralized config
  const PUBLISHABLE_KEY = clerkPublishableKey;

  // Validate environment and log info (development only)
  useEffect(() => {
    validateEnvironment();
    logEnvironmentInfo();
  }, []);

  useEffect(() => {
    // Global keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K for global search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
      
      // Ctrl+Shift+T to trigger session timeout (testing only)
      if (e.ctrlKey && e.shiftKey && e.key === 'T') {
        setSessionTimeout(true);
      }
    };

    // Visible search trigger (sidebar) dispatches this instead of prop-drilling
    // setIsSearchOpen through the route tree — same modal, same open path as ⌘K.
    const handleOpenSearch = () => setIsSearchOpen(true);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('open-global-search', handleOpenSearch);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('open-global-search', handleOpenSearch);
    };
  }, []);

  useEffect(() => {
    // Session timeout after 30 minutes of inactivity (1800000ms)
    // For testing: Change to 60000 (1 minute) to see the modal quickly
    const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes
    
    let timer: NodeJS.Timeout;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setSessionTimeout(true);
      }, SESSION_DURATION);
    };

    // Initialize timer
    resetTimer();

    // Reset timer on user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      if (!sessionTimeout) {
        resetTimer();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      clearTimeout(timer);
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [sessionTimeout]);

  return (
    <BrowserRouter>
      {/* reducedMotion="user" makes every motion.* component in the app (page
          load, Pipeline stage-change, save confirmation, nav hover) honor
          prefers-reduced-motion automatically — one place, not per-component. */}
      <MotionConfig reducedMotion="user">
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        afterSignOutUrl="/"
        telemetry={false}
        appearance={{
          // Suppress development mode warnings in console and UI
          layout: {
            unsafe_disableDevelopmentModeWarnings: true,
          },
        }}
      >
        <QueryProvider>
          <ThemeProvider>
            <AuthProvider>
              <OrganizationProvider>
                <DemoModeProvider>
                <FeatureFlagsProvider>
                  <ErrorBoundary>
                    <PostHogProvider>
                      {/* Chrome Extension Auth Handler */}
                      <ExtensionAuthHandler />

                      {/* Skip to main content for keyboard navigation */}
                      <a href="#main-content" className="skip-link">
                        Skip to main content
                      </a>

                      <AppRoutes />
                      <Toaster position="top-right" richColors duration={3500} />
                      <SessionTimeoutModal
                        isOpen={sessionTimeout}
                        onClose={() => setSessionTimeout(false)}
                      />
                      <GlobalSearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
                    </PostHogProvider>
                  </ErrorBoundary>
                </FeatureFlagsProvider>
                </DemoModeProvider>
              </OrganizationProvider>
            </AuthProvider>
          </ThemeProvider>
        </QueryProvider>
      </ClerkProvider>
      </MotionConfig>
    </BrowserRouter>
  );
}